import { motion } from "framer-motion";
import { ABOUT } from "@/constants/testIds";

const milestones = [
  { year: "1996", event: "Founded in Scarborough by three IUEC-certified mechanics." },
  { year: "2003", event: "Opened Etobicoke depot — GTA-wide 24/7 response begins." },
  { year: "2011", event: "First MRL installation on Queen St. West — 26-storey mixed-use." },
  { year: "2018", event: "Launched destination-dispatch modernization program." },
  { year: "2024", event: "500th elevator commissioned across Toronto & Hamilton." },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid={ABOUT.root}
      className="relative bg-[#0A0A0A] text-white border-b border-[#0A0A0A] overflow-hidden"
    >
      {/* Skyline backdrop */}
      <div
        className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1517935706615-2717063c2225?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHx0b3JvbnRvJTIwc2t5bGluZSUyMGJ1aWxkaW5nc3xlbnwwfHx8fDE3ODMxMjU1NDl8MA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 vx-grid-overlay-dark pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FF5E00] mb-6">
              Our Heritage · Since 1996
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter"
            >
              Nearly 30 years
              <br />
              moving Toronto
              <br />
              <span className="text-[#FF5E00]">upward.</span>
            </motion.h2>

            <p className="mt-8 text-white/70 text-base lg:text-lg leading-relaxed max-w-lg">
              We were founded the year the CN Tower turned twenty — three
              mechanics with a service van and a promise to answer every call
              inside an hour. Nearly three decades later, that promise still
              defines every job we take on.&nbsp;
            </p>

            <p className="mt-4 text-white/70 text-base lg:text-lg leading-relaxed max-w-lg">
              Independent. Family-run. TSSA-licensed. We work alongside
              Toronto&rsquo;s architects, developers and property managers to design,
              install and maintain elevator systems that outlast the buildings
              they serve.&nbsp;
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <div className="border border-white/20 px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/50">
                  Headquarters
                </div>
                <div className="mt-1 font-display font-bold text-lg">Scarborough, ON</div>
              </div>
              <div className="border border-white/20 px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/50">
                  Service Region
                </div>
                <div className="mt-1 font-display font-bold text-lg">GTA + Hamilton</div>
              </div>
              <div className="border border-white/20 px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/50">
                  Team
                </div>
                <div className="mt-1 font-display font-bold text-lg">42 Mechanics</div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="lg:col-span-6 lg:pl-8 lg:border-l border-white/15">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/50 mb-8">
              Milestones
            </div>
            <ol className="relative">
              {milestones.map((m, i) => (
                <motion.li
                  key={m.year}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="grid grid-cols-[80px_1fr] gap-6 py-6 border-t border-white/10 first:border-t-0"
                >
                  <div className="font-display font-black text-2xl md:text-3xl text-[#FF5E00] tracking-tighter">
                    {m.year}
                  </div>
                  <div className="text-white/80 text-base leading-relaxed">{m.event}</div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
